import type { BlockDto } from '@klickibunti/pagebuilder-react';
import { HeroCardPrices } from './HeroCardPrices';
import { HeroCardSearchCentered } from './HeroCardSearchCentered';
import { HeroCardTabbedCity } from './HeroCardTabbedCity';
import { HeroCardTabbedHome } from './HeroCardTabbedHome';
import { HeroCardText } from './HeroCardText';
import { isHeroCardBlock } from './types';
import type {
  HeroCardPricesConfig,
  HeroCardSearchCenteredConfig,
  HeroCardTabbedCityConfig,
  HeroCardTabbedHomeConfig,
  HeroCardTextConfig,
} from './types';

interface HeroCardSlotProps {
  /** The block `HeroComponent` picked out of `widgets.hero.content`. */
  block: BlockDto;
  /** The hero's slot geometry, applied to whichever card's own root element. */
  className: string;
  /**
   * The Hero's own h1, for the full-width cards that host it inside the panel
   * instead of leaving it over the image. Ignored by every other card.
   */
  heading?: React.ReactNode;
}

/**
 * Renders whichever Hero Card an Editor placed in the Hero's content zone.
 *
 * The two text schemas resolve to the same `HeroCardText` with different
 * `align` props — the schema split exists only so the Editor's component
 * choice carries the typography (SH-312 Q3/Q4).
 *
 * `data-strapi-block-*` is stamped here rather than by `SlotRenderer`, which
 * these blocks bypass (see `types.ts`); every card spreads it onto its root.
 */
export function HeroCardSlot({ block, className, heading }: HeroCardSlotProps) {
  if (!isHeroCardBlock(block)) {
    return null;
  }

  const props = {
    className,
    'data-strapi-block-type': block.componentType,
    'data-strapi-block-id': block.id,
  };

  switch (block.componentType) {
    case 'widgets.hero-card-text':
      return <HeroCardText config={block as HeroCardTextConfig} {...props} />;
    case 'widgets.hero-card-text-centered':
      return (
        <HeroCardText
          config={block as HeroCardTextConfig}
          align="center"
          {...props}
        />
      );
    case 'widgets.hero-card-tabbed-home':
      return (
        <HeroCardTabbedHome
          config={block as HeroCardTabbedHomeConfig}
          {...props}
        />
      );
    case 'widgets.hero-card-tabbed-city':
      return (
        <HeroCardTabbedCity
          config={block as HeroCardTabbedCityConfig}
          {...props}
        />
      );
    case 'widgets.hero-card-prices':
      return (
        <HeroCardPrices config={block as HeroCardPricesConfig} {...props} />
      );
    case 'widgets.hero-card-search-centered':
      return (
        <HeroCardSearchCentered
          config={block as HeroCardSearchCenteredConfig}
          heading={heading}
          {...props}
        />
      );
  }
}
